import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  CalendarBlank,
  GoogleLogo,
  ArrowsClockwise,
  CheckCircle,
  Clock,
  Lightning,
  ArrowSquareOut,
  WarningCircle, Calendar} from '@phosphor-icons/react';
import { getCalendarStatus, getCalendarEvents, startCalendarOAuth, disconnectCalendar, syncTasksToCalendar, syncSchedulesToCalendar, rescheduleCalendarEvent } from '../services/api';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { PageHero } from '../components/motion/PageHero';

const dayNames = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];
const monthNames = ['Janvier', 'Fevrier', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Aout', 'Septembre', 'Octobre', 'Novembre', 'Decembre'];

function getDaysInMonth(year, month) {
  return new Date(year, month + 1, 0).getDate();
}

function getFirstDayOfMonth(year, month) {
  return new Date(year, month, 1).getDay();
}

export default function CalendarPage() {
  const [status, setStatus] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [viewMode, setViewMode] = useState('month');
  const [currentDate, setCurrentDate] = useState(new Date());

  useEffect(() => {
    fetchStatus();
  }, []);

  const fetchStatus = async () => {
    try {
      const res = await getCalendarStatus();
      setStatus(res.data);
      if (res.data.connected) {
        fetchEvents();
      }
    } catch {
      setStatus({ connected: false, configured: false });
    } finally {
      setLoading(false);
    }
  };

  const fetchEvents = async () => {
    try {
      const res = await getCalendarEvents(60);
      setEvents(res.data.events || []);
    } catch (err) {
      console.error('Error fetching events:', err);
    }
  };

  const handleConnect = async () => {
    try {
      const res = await startCalendarOAuth();
      window.location.href = res.data.authorization_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur de connexion');
    }
  };

  const handleDisconnect = async () => {
    try {
      await disconnectCalendar();
      setStatus({ connected: false, configured: status?.configured });
      setEvents([]);
      toast.success('Google Calendar deconnecte');
    } catch {
      toast.error('Erreur');
    }
  };

  const handleSyncTasks = async () => {
    setSyncing(true);
    try {
      const res = await syncTasksToCalendar();
      toast.success(`${res.data.synced} tache(s) synchronisee(s)`);
      fetchEvents();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur de sync');
    } finally {
      setSyncing(false);
    }
  };

  const handleSyncSchedules = async () => {
    setSyncing(true);
    try {
      const res = await syncSchedulesToCalendar();
      toast.success(`${res.data.synced} echeance(s) synchronisee(s)`);
      fetchEvents();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur de sync');
    } finally {
      setSyncing(false);
    }
  };

  const getEventsForDate = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    return events.filter(e => {
      const start = (e.start || '').substring(0, 10);
      return start === dateStr;
    });
  };

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const daysInMonth = getDaysInMonth(year, month);
  const firstDay = getFirstDayOfMonth(year, month);

  const prevMonth = () => setCurrentDate(new Date(year, month - 1, 1));
  const nextMonth = () => setCurrentDate(new Date(year, month + 1, 1));
  const today = new Date();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-[#2D5A4C]" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="calendar-page">
      {/* Hero header — UX transversale v2 */}
      <PageHero
        title="Calendrier"
        subtitle="Événements, échéances et missions coopératives"
        icon={<Calendar size={24} weight="duotone" className="text-white" />}
        gradient="from-[#00AEEF] to-[#2D5A4C]"
        accents={['#D5007D', '#80C51F']}
      />

      {/* Not configured warning */}
      {!status?.configured && !status?.connected && (
        <Card className="border-amber-200 bg-amber-50" data-testid="gcal-not-configured">
          <CardContent className="p-4 flex items-start gap-3">
            <WarningCircle size={20} className="text-amber-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-amber-800">Configuration requise</p>
              <p className="text-xs text-amber-700 mt-1">
                Allez dans Parametres pour configurer les identifiants Google Calendar (Client ID et Client Secret).
                Vous devez creer un projet Google Cloud avec l'API Calendar activee.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Calendar View */}
      <Card className="border-[#E8E6E1] shadow-sm" data-testid="calendar-view">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="sm" onClick={prevMonth}>&lt;</Button>
              <CardTitle className="text-lg font-semibold text-[#1A2421]" style={{ fontFamily: 'Manrope' }}>
                {monthNames[month]} {year}
              </CardTitle>
              <Button variant="ghost" size="sm" onClick={nextMonth}>&gt;</Button>
            </div>
            <Button variant="outline" size="sm" onClick={() => setCurrentDate(new Date())} data-testid="today-btn">
              Aujourd'hui
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* Day headers */}
          <div className="grid grid-cols-7 gap-px mb-1">
            {dayNames.map(d => (
              <div key={d} className="text-center text-[10px] font-bold uppercase tracking-wider text-[#5E6D66] py-2">
                {d}
              </div>
            ))}
          </div>

          {/* Calendar grid */}
          <div className="grid grid-cols-7 gap-px bg-[#E8E6E1] border border-[#E8E6E1] rounded-lg overflow-hidden">
            {/* Empty cells for days before month start */}
            {Array.from({ length: firstDay }, (_, i) => (
              <div key={`empty-${i}`} className="bg-[#FAFAF8] min-h-[80px] sm:min-h-[100px] p-1" />
            ))}

            {/* Days of the month */}
            {Array.from({ length: daysInMonth }, (_, i) => {
              const day = i + 1;
              const date = new Date(year, month, day);
              const isToday = day === today.getDate() && month === today.getMonth() && year === today.getFullYear();
              const dayEvents = getEventsForDate(date);

              return (
                <div
                  key={day}
                  onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('bg-[#E8F0EC]'); }}
                  onDragLeave={(e) => { e.currentTarget.classList.remove('bg-[#E8F0EC]'); }}
                  onDrop={async (e) => {
                    e.currentTarget.classList.remove('bg-[#E8F0EC]');
                    const eventId = e.dataTransfer.getData('eventId');
                    if (!eventId) return;
                    const newDate = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                    try {
                      await rescheduleCalendarEvent(eventId, newDate);
                      toast.success('Événement reprogrammé');
                      fetchEvents();
                    } catch (err) {
                      toast.error(err.response?.data?.detail || 'Erreur reprogrammation');
                    }
                  }}
                  className={`bg-white min-h-[80px] sm:min-h-[100px] p-1.5 transition-colors hover:bg-[#F9F8F5] ${isToday ? 'ring-2 ring-inset ring-[#2D5A4C]' : ''}`}
                  data-testid={`calendar-day-${day}`}
                >
                  <span className={`text-xs font-medium inline-flex items-center justify-center w-6 h-6 rounded-full ${
                    isToday ? 'bg-[#2D5A4C] text-white' : 'text-[#1A2421]'
                  }`}>
                    {day}
                  </span>
                  <div className="mt-0.5 space-y-0.5">
                    {dayEvents.slice(0, 3).map((ev, idx) => {
                      const isCRM = ev.summary?.startsWith('[CRM]') || ev.summary?.startsWith('[Paiement]');
                      return (
                        <div
                          key={idx}
                          draggable={!!ev.id}
                          onDragStart={(e) => { e.dataTransfer.setData('eventId', ev.id || ''); e.dataTransfer.effectAllowed = 'move'; }}
                          className={`text-[9px] sm:text-[10px] px-1 py-0.5 rounded truncate cursor-move ${
                            isCRM
                              ? 'bg-[#2D5A4C]/10 text-[#2D5A4C]'
                              : 'bg-blue-50 text-blue-700'
                          }`}
                          title={`${ev.summary} — Glissez pour reprogrammer`}
                          data-testid={`calendar-event-${ev.id}`}
                        >
                          {ev.summary}
                        </div>
                      );
                    })}
                    {dayEvents.length > 3 && (
                      <span className="text-[9px] text-[#5E6D66]">+{dayEvents.length - 3}</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Upcoming Events */}
      {status?.connected && (
        <Card className="border-[#E8E6E1] shadow-sm" data-testid="upcoming-events">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold text-[#1A2421] flex items-center gap-2">
              <Clock size={18} className="text-[#2D5A4C]" />
              Evenements a venir ({events.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {events.length === 0 ? (
              <p className="text-sm text-[#5E6D66] text-center py-6">Aucun evenement a venir</p>
            ) : (
              <div className="space-y-2 max-h-[400px] overflow-y-auto">
                {events.slice(0, 20).map((ev) => {
                  const isCRM = ev.summary?.startsWith('[CRM]') || ev.summary?.startsWith('[Paiement]');
                  const startDate = ev.start ? new Date(ev.start) : null;

                  return (
                    <div
                      key={ev.id}
                      className="flex items-center gap-3 p-3 border border-[#E8E6E1] rounded-lg hover:bg-[#F9F8F5] transition-colors"
                      data-testid={`event-${ev.id}`}
                    >
                      <div className={`w-1 h-10 rounded-full flex-shrink-0 ${isCRM ? 'bg-[#2D5A4C]' : 'bg-blue-500'}`} />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-[#1A2421] truncate">{ev.summary}</p>
                        <div className="flex items-center gap-2 mt-0.5">
                          <span className="text-[10px] text-[#5E6D66]">
                            {startDate ? startDate.toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' }) : ''}
                          </span>
                          {isCRM && <Badge className="text-[9px] bg-[#2D5A4C]/10 text-[#2D5A4C]">CRM</Badge>}
                        </div>
                      </div>
                      {ev.html_link && (
                        <a href={ev.html_link} target="_blank" rel="noopener noreferrer" className="p-1.5 hover:bg-[#F0EFEB] rounded">
                          <ArrowSquareOut size={14} className="text-[#5E6D66]" />
                        </a>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
